import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TouchSequence } from "selenium-webdriver";

import { BenchmarkSeries, BenchmarkSeriesItem, BenchmarkSeriesModel } from "src/app/shared/benchmarks/benchmark-series";
import { DataService } from "src/app/shared/network/data.service";
import { NotificationService } from "src/app/shared/notificaton.service";
import { ProjectService } from "src/app/shared/project/project.service";

export class Coord {
    public row: number;
    public col: number;
}

@Component({
    templateUrl: './series.component.html',
    styleUrls: ['../general.scss', './series.component.scss']
})
export class SeriesComponent implements OnInit {
    private projectId: string;
    public loading = true;
    public oldControlValue: string;
    public newControlValue: string;
    public series: BenchmarkSeries[];
    public movingItem: Coord;
    public multiSelectStartedAt: Coord;
    public multiSelectEndedAt: Coord;
    public haveEligibleForForecast = false;
    public selectedAsForecast = [];
    public selectedSeriesName: string;
    public differentSeriesSelected: boolean;
    public previousSelectedSeries: string;

    constructor(private activatedRoute: ActivatedRoute, private router: Router, private dataService: DataService, private projectService: ProjectService, private notificationService: NotificationService ) {
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe((queryParams: any) => {
            let projectId = queryParams.projectId;
            if (projectId) {
                this.projectId = projectId;
                this.projectService.setProjectId(projectId);
                this.dataService.get<BenchmarkSeriesModel>(`benchmarks/series/${projectId}`).then(res => {
                    for (let i = 0; i < res.series.length; ++i) {
                        res.series[i].renamingName = res.series[i].name;
                    }
                    this.series = res.series;
                    this.markItemsForMultiSelect();
                    this.oldControlValue = res.oldControlValue;
                    this.newControlValue = res.newControlValue;
                    this.loadSelectedAsForecastList();
                    this.selectedSeriesName = res.selectedSeriesName;
                    this.differentSeriesSelected = res.differentSeriesSelected;
                    this.previousSelectedSeries = res.previousSelectedSeries;
                    this.loading = false;
                });
            }
        });
    }

    async saveSeries() {
        if (this.loading) {
            return;
        }
        try {
            await this.dataService.post('benchmarks/saveSeries', {
                projectId: this.projectId,
                series: this.series,
                selectedSeriesName: this.selectedSeriesName
            });
            this.router.navigateByUrl('/cards?projectId=' + this.projectId);
        } finally {
            this.loading = false;
        }
    }

    public finishRenaming(series: BenchmarkSeries): void {
        for (let i = 0; i < this.series.length; ++i) {
            if (series === this.series[i]) {
                continue;
            }
            if (series.renamingName == this.series[i].name) {
                this.notificationService.showError("Series name must be unique.");
                return;
            }
        }
        if (this.selectedSeriesName == series.name) {
            this.selectedSeriesName = series.renamingName;
        }
        series.name = series.renamingName;
        series.renaming = false;
    }

    selectAsForecast(item: BenchmarkSeriesItem): void {
        if (item.isCurrentlyEligibleAsForecast) {
            item.isSelectedAsForecast = true;
        }
        this.loadSelectedAsForecastList();
    }

    unSelectAsForecast(item: BenchmarkSeriesItem): void {
        item.isSelectedAsForecast = false;
        this.loadSelectedAsForecastList();
    }

    handleCurrentEligibilityAsForecast() {
        if (!this.series) {
            return;
        }
        
        let seriesIxWithSelectedAsForecast: number = undefined;
        for (let i = 0; i < this.series.length; ++i) {
            for (let j = 0; j < this.series[i].items.length; ++j) {
                if (this.series[i].items[j] && this.series[i].items[j].isSelectedAsForecast) {
                    seriesIxWithSelectedAsForecast = i;
                    break;
                }
            }
        }
        for (let i = 0; i < this.series.length; ++i) {
            for (let j = 0; j < this.series[i].items.length; ++j) {
                if (this.series[i].items[j]) {
                    this.series[i].items[j].isCurrentlyEligibleAsForecast = this.series[i].items[j].isEligibleAsForecast && (seriesIxWithSelectedAsForecast === undefined || seriesIxWithSelectedAsForecast == i);
                }
            }
        }
    }

    loadSelectedAsForecastList(): void {
        if (!this.series) {
            return;
        }
        let haveEligible = false;
        let result: string[] = [];
        for (let i = 0; i < this.series.length; ++i) {
            for (let j = 0; j < this.series[i].items.length; ++j) {
                if (this.series[i].items[j]) {
                    if (this.series[i].items[j].isSelectedAsForecast) {
                        result.push(this.series[i].items[j].controlValue);
                    }
                    if (this.series[i].items[j].isEligibleAsForecast) {
                        haveEligible = true;
                    }
                }
            }
        }
        this.selectedAsForecast = result;
        this.haveEligibleForForecast = haveEligible;
        this.handleCurrentEligibilityAsForecast();
    }

    startMovingItem(row: number, column: number): void {
        if (this.series[row].items[column].isSelectedAsForecast) {
            return;
        }
        this.movingItem = {
            row: row,
            col: column,
        };
    }

    swapWith(row: number, col: number): void {
        if (this.movingItem) {
            let tmp = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = this.series[row].items[col];
            this.series[row].items[col] = tmp;
            this.movingItem = undefined;
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let count = this.multiSelectEndedAt.col - this.multiSelectStartedAt.col + 1;
            for (let i = 0; i < count; ++i) {
                let tmp = this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col];
                this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col] = this.series[row].items[i + col];
                this.series[row].items[i + col] = tmp;
            }
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    insertLeftFrom(row: number, col: number): void {
        if (this.movingItem) {
            let selected = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = null;
            for (let i = 0; i < this.series.length; ++i) {
                this.series[i].items.splice(col, 0, null);
            }
            this.series[row].items[col] = selected;
            this.movingItem = undefined;
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let count = this.multiSelectEndedAt.col - this.multiSelectStartedAt.col + 1;
            let selected = [];
            for (let i = 0; i < count; ++i) {
                selected.push(this.series[this.multiSelectStartedAt.row].items[this.multiSelectStartedAt.col + i]);
                this.series[this.multiSelectStartedAt.row].items[this.multiSelectStartedAt.col + i] = null;
            }
            for (let i = 0; i < count; ++i) {
                for (let j = 0; j < this.series.length; ++j) {
                    this.series[j].items.splice(col, 0, null);
                }
            }
            for (let i = 0; i < count; ++i) {
                this.series[row].items[i + col] = selected[i];
            }
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    insertRightFrom(row: number, col: number): void {
        if (this.movingItem) {
            let selected = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = null;
            for (let i = 0; i < this.series.length; ++i) {
                this.series[i].items.splice(col + 1, 0, null);
            }
            this.series[row].items[col + 1] = selected;
            this.movingItem = undefined;
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let count = this.multiSelectEndedAt.col - this.multiSelectStartedAt.col + 1;
            let selected = [];
            for (let i = 0; i < count; ++i) {
                selected.push(this.series[this.multiSelectStartedAt.row].items[this.multiSelectStartedAt.col + i]);
                this.series[this.multiSelectStartedAt.row].items[this.multiSelectStartedAt.col + i] = null;
            }
            for (let j = 0; j < this.series.length; ++j) {
                for (let i = 0; i < count; ++i) {
                    this.series[j].items.splice(col + 1, 0, null);
                }
            }
            for (let i = 0; i < count; ++i) {
                this.series[row].items[1 + i + col] = selected[i];
            }
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    insertAbove(row: number, col: number): void {
        if (this.movingItem) {
            let selected = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = null;
            this.series.splice(row, 0, this.createNewSeries(this.series));
            this.series[row].items[col] = selected;
            this.movingItem = undefined;
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let newSeries = this.createNewSeries(this.series);
            for (let i = 0; i <= (this.multiSelectEndedAt.col - this.multiSelectStartedAt.col); ++i) {
                newSeries.items[i + col] = this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col];
                this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col] = null;
            }
            this.series.splice(row, 0, newSeries);
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    insertBelow(row: number, col: number): void {
        if (this.movingItem) {
            let selected = this.series[this.movingItem.row].items[this.movingItem.col];
            this.series[this.movingItem.row].items[this.movingItem.col] = null;
            this.series.splice(row + 1, 0, this.createNewSeries(this.series));
            this.series[row + 1].items[col] = selected;
            this.movingItem = undefined;
        } else if (this.multiSelectEndedAt) {
            this.addColumnsIfRequired(col);
            let newSeries = this.createNewSeries(this.series);
            for (let i = 0; i <= (this.multiSelectEndedAt.col - this.multiSelectStartedAt.col); ++i) {
                newSeries.items[i + col] = this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col];
                this.series[this.multiSelectStartedAt.row].items[i + this.multiSelectStartedAt.col] = null;
            }
            this.series.splice(row + 1, 0, newSeries);
            this.cancelMultiSelect();
        }
        this.removeEmptyRowsColumns();
        this.markItemsForMultiSelect();
    }

    private addColumnsIfRequired(targetIx: number) {
        //add columns if required on bulk movement
        let count = this.multiSelectEndedAt.col - this.multiSelectStartedAt.col + 1
        let columnsToAdd = count + targetIx - this.series[0].items.length;
        for (let i = 0; i < columnsToAdd; ++i) {
            for (let j = 0; j < this.series.length; ++j) {
                this.series[j].items.splice(this.series[j].items.length, 0, null);
            }
        }
    }

    createNewSeries(series: BenchmarkSeries[]): BenchmarkSeries {
        let newSeries = new BenchmarkSeries();
        let name: string;
        for (let i = 1; i <= series.length + 1; ++i) {
            name = "Series " + i;
            let exists = false;
            for (let j = 0; j < series.length; ++j) {
                if (series[j].name == name) {
                    exists = true;
                    break;
                }
            }
            if (!exists) {
                break;
            }
        }
        newSeries.name = name;
        newSeries.items = [];
        for (let i = 0; i < series[0].items.length; ++i) {
            newSeries.items.push(null);
        }
        return newSeries;
    }

    removeEmptyRowsColumns(): void {
        let emptyRows = this.getEmptyRows(this.series);
        for (let k = emptyRows.length - 1; k >= 0; --k) {
            let ix = emptyRows[k];
            if (!this.series[ix].isExclude) {
                this.series.splice(ix, 1);
            }
        }
        let emptyCols = this.getEmptyCols(this.series);
        for (let k = emptyCols.length - 1; k >= 0; --k) {
            let ix = emptyCols[k];
            for (let i = 0; i < this.series.length; ++i) {
                this.series[i].items.splice(ix, 1);
            }
        }
        this.handleCurrentEligibilityAsForecast();
    }

    private getEmptyRows(series: BenchmarkSeries[]): number[] {
        let emptyRows = [];
        for (let i = 0; i < series.length; ++i) {
            let allEmpty = true;
            for (let j = 0; j < series[i].items.length; ++j) {
                if (series[i].items[j]) {
                    allEmpty = false;
                    break;
                }
            }
            if (allEmpty) {
                emptyRows.push(i);
            }
        }
        emptyRows.sort();
        return emptyRows;
    }

    private getEmptyCols(series: BenchmarkSeries[]): number[] {
        let emptyCols = [];
        for (let j = 0; j < series[0].items.length; ++j) {
            let allEmpty = true;
            for (let i = 0; i < series.length; ++i) {
                if (series[i].items[j]) {
                    allEmpty = false;
                    break;
                }
            }
            if (allEmpty) {
                emptyCols.push(j);
            }
        }
        return emptyCols;
    }

    private markItemsForMultiSelect() {
        for (let ser of this.series) {
            if (ser.items.length < 2) {
                return;
            }
            for (let i = 0; i < ser.items.length; ++i) {
                if (!ser.items[i]) {
                    continue;
                }
                if (i == 0) {
                    if (ser.items[i + 1]) {
                        ser.items[i].canStartMultiSelect = true;
                    }
                } else {
                    if (ser.items[i - 1]) {
                        ser.items[i].canStartMultiSelect = true;
                    }
                }
            }
        }
    }

    public startMultiSelect(rowIx: number, colIx: number) {
        this.movingItem = undefined;
        this.multiSelectStartedAt = {
            row: rowIx,
            col: colIx,
        }
        this.series[rowIx].items[colIx].isMultiSelected = true;

        for (let i = colIx - 1; i >= 0; --i) {
            if (!this.series[rowIx].items[i]) {
                break;
            }
            this.series[rowIx].items[i].canEndMultiSelect = true;
        }
        for (let i = colIx + 1; i < this.series[rowIx].items.length; ++i) {
            if (!this.series[rowIx].items[i]) {
                break;
            }
            this.series[rowIx].items[i].canEndMultiSelect = true;
        }
    }

    public cancelMultiSelect() {
        for (let ser of this.series) {
            for (let item of ser.items) {
                if (item) {
                    item.isMultiSelected = false;
                    item.canEndMultiSelect = false;
                }
            }
        }
        this.multiSelectStartedAt = undefined;
        this.multiSelectEndedAt = undefined;
    }

    public endMultiSelect(rowIx: number, colIx: number) {
        if (!this.multiSelectStartedAt || this.multiSelectStartedAt.row != rowIx) {
            //something is wrong
            this.cancelMultiSelect();
            this.markItemsForMultiSelect();
            return;
        }

        let minColIx = Math.min(colIx, this.multiSelectStartedAt.col);
        let maxColIx = Math.max(colIx, this.multiSelectStartedAt.col);
        this.multiSelectStartedAt = {
            row: rowIx,
            col: minColIx,
        };
        this.multiSelectEndedAt = {
            row: rowIx,
            col: maxColIx,
        };
        for (let i = minColIx; i <= maxColIx; ++i) {
            this.series[rowIx].items[i].isMultiSelected = true;
        }
    }

    public toggleSeriesSelection(seriesName: string, $event: Event) {
        console.log($event);
        $event.preventDefault();
        console.log(this.selectedSeriesName);
        this.selectedSeriesName = seriesName;
        console.log(this.selectedSeriesName);
    }
}
